import type moment from 'moment-timezone';

/**
 * The `POST /survey/insertMicrosite` request body. Field names and value
 * shapes are the ShowTracker API contract (showtracker-api,
 * `src/services/Microsite`), unchanged by the SA-1681 migration — the
 * builders below are pure so the wire shape can be pinned by unit tests.
 */
export interface MicrositeSurveyPayload {
  // System/non-UI fields
  show_id: number;
  start_date: string;
  end_date: string;
  specialist_id: string | null;
  team_id: number;
  survey_date: Date;
  device_survey_guid: string;
  device_id: string;
  survey_type: string;
  app_version: string;
  abandoned: number;
  start_time: Date;
  end_time: Date;

  // Custom data / non FFS questions
  custom_data: string; // JSON stringified object

  // Personal info
  first_name: string;
  last_name: string;
  business_name: string | null;
  address1: string;
  address2: string | null;
  city: string;
  state: string;
  country_code: string;
  zip_code: string;

  // Contact info
  phone: string | null;
  email: string;
  email_opt_in: number;
  accepts_sms: number;
  gender: string | null;
  age_bracket: string | null;

  // Other fields
  vehicle_driven_most_make_id: number | null;
  in_market_timing: string | null;
  personal_or_business: string | null;
  language: string;
  industry: string | null;

  // Signature/Waiver
  waiver_type: string;
  signature: string | null;
  minor_signature: string | null;

  // Vehicles of interest
  vehicles_of_interest: number[];
}

export interface PayloadShowContext {
  startDate: moment.Moment;
  endDate: moment.Moment;
  teamID: number;
  isCanadian: boolean;
}

/**
 * `minorNames` is the app's own `paneldynamic` (`[{ minorName }]`); the API
 * reads the compacted `minor_name_N` keys out of `custom_data`.
 */
export function compactMinorNames(minorNames: unknown): Record<string, string> {
  if (!Array.isArray(minorNames)) return {};
  return minorNames
    .map((panel: any) => panel?.minorName)
    .filter((name: unknown): name is string => typeof name === 'string' && name.trim().length > 0)
    .reduce((acc: Record<string, string>, name: string, idx: number) => {
      acc[`minor_name_${idx + 1}`] = name.trim();
      return acc;
    }, {});
}

export function countMinors(surveyData: Record<string, any>): number {
  return surveyData.minorsYesNo === '1' ? Object.keys(compactMinorNames(surveyData.minorNames)).length : 0;
}

/**
 * Builds the completed-survey payload from `survey.data` (with every
 * question's value merged in by the caller). `now` is injectable so tests
 * can pin `end_time`.
 */
export function buildSurveyPayload(
  surveyData: Record<string, any>,
  show: PayloadShowContext,
  source: string,
  language: string,
  now: Date = new Date(),
): MicrositeSurveyPayload {
  return {
    // System/non-UI fields
    show_id: surveyData.show_id,
    start_date: show.startDate.format('YYYY-MM-DD'),
    end_date: show.endDate.format('YYYY-MM-DD'),
    specialist_id: surveyData.specialist_id,
    team_id: show.teamID,
    survey_date: surveyData.survey_date,
    device_survey_guid: surveyData.device_survey_guid,
    device_id: surveyData.device_id,
    survey_type: source,
    app_version: surveyData.app_version,
    abandoned: surveyData.abandoned || 0,
    start_time: surveyData.start_time,
    end_time: now,

    // Custom data / non FFS questions
    custom_data: JSON.stringify({
      _utm: surveyData._utm,
      _referrer: surveyData._referrer,
      _language: surveyData._language,
      _screenWidth: surveyData._screenWidth,
      _offset: surveyData._offset,
      _timeZone: surveyData._timeZone,
      why_are_you_here: surveyData.why_are_you_here || null,
      shows_attended: surveyData.shows_attended || null,
      preferred_language: surveyData.preferred_language || null,
      highest_level_education: surveyData.highest_level_education || null,
      hispanic_latino: surveyData.hispanic_latino || null,
      ethnicity_race: surveyData.ethnicity_race?.includes('none')
        ? ['Prefer not to answer']
        : surveyData.ethnicity_race?.length
          ? surveyData.ethnicity_race
          : null,
      device_name: surveyData.device_name || null,
      // Map paneldynamic minorNames array to numbered minor_name_N keys
      ...compactMinorNames(surveyData.minorNames),
      passion: surveyData.passion || null,
      capability: surveyData.capability || null,
    }),

    // Personal info
    first_name: surveyData.first_name,
    last_name: surveyData.last_name,
    business_name: surveyData.business_name || null,
    address1: surveyData.address_group?.address1,
    address2: null,
    city: surveyData.address_group?.city,
    state: surveyData.address_group?.state,
    country_code: show.isCanadian ? 'CAN' : 'USA',
    zip_code: surveyData.address_group?.zip_code,

    // Contact info
    phone: surveyData.phone,
    email: surveyData.email,
    // SA-1680: the About You opt-in checkbox is gone on Ford US / Lincoln US,
    // replaced by a disclosure stating the visitor *is* opting in — so the
    // stored value says the same unconditionally, mirroring the iOS app
    // (SA-1681, surveySplash.tsx `acceptsContact: true`). Canada's own
    // consent gate still sets `fordEmailOptIn` explicitly on accept, and an
    // explicit reject never reaches this builder (it records
    // `buildAbandonedPayload` instead), so this is safe for every real path.
    email_opt_in: 1,
    accepts_sms: 0,
    gender: surveyData.gender || null,
    age_bracket: surveyData.ageBracket || null,

    // Other fields
    vehicle_driven_most_make_id: surveyData.vehicleDrivenMostMake || null,
    in_market_timing: surveyData.inMarketTiming || null,
    personal_or_business: surveyData.personal_or_business || null,
    // SurveyJS reports its default locale as '' — record English explicitly.
    language: language || 'en',
    industry: surveyData.industry || null,

    // Signature/Waiver
    waiver_type: source,
    signature: surveyData.signature || null,
    minor_signature: surveyData.minorSignature || null,

    // Vehicles of interest
    vehicles_of_interest: surveyData.voi || [],
  };
}

/**
 * The analytics-only record written when a Canadian visitor declines
 * consent (`consentReject`). `getValue` reads the live survey's seeded
 * system values.
 */
export function buildAbandonedPayload(
  getValue: (name: string) => any,
  show: PayloadShowContext,
  locale: string,
  now: Date = new Date(),
): MicrositeSurveyPayload {
  return {
    show_id: getValue('show_id'),
    start_date: show.startDate.format('YYYY-MM-DD'),
    end_date: show.endDate.format('YYYY-MM-DD'),
    specialist_id: getValue('specialist_id') || '',
    team_id: show.teamID,
    survey_date: getValue('survey_date'),
    device_survey_guid: getValue('device_survey_guid'),
    device_id: getValue('device_id'),
    survey_type: getValue('source'),
    app_version: getValue('app_version'),
    abandoned: 1,
    start_time: getValue('start_time'),
    end_time: now,
    first_name: 'ABANDONED',
    last_name: '',
    business_name: null,
    address1: '',
    address2: null,
    city: '',
    state: '',
    country_code: 'CAN',
    zip_code: '',
    phone: null,
    email: '',
    email_opt_in: 0,
    accepts_sms: 0,
    gender: null,
    age_bracket: null,
    vehicle_driven_most_make_id: null,
    in_market_timing: null,
    personal_or_business: null,
    // SurveyJS reports its default locale as '' — record English explicitly.
    language: locale || 'en',
    industry: null,
    waiver_type: getValue('source'),
    signature: null,
    minor_signature: null,
    custom_data: JSON.stringify({
      _utm: getValue('_utm'),
      _referrer: getValue('_referrer'),
      _language: getValue('_language'),
      _screenWidth: getValue('_screenWidth'),
      _offset: getValue('_offset'),
      _timeZone: getValue('_timeZone'),
    }),
    vehicles_of_interest: [],
  };
}
